import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const About = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.fade-in, .slide-in-left, .slide-in-right').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const stats = [
    { number: '10+', label: 'Years of Experience', description: 'Serving happiness since 2014' },
    { number: '50+', label: 'Unique Flavors', description: 'From traditional to innovative' },
    { number: '1M+', label: 'Happy Customers', description: 'And counting every day' },
    { number: '100%', label: 'Natural Ingredients', description: 'No artificial preservatives' }
  ];

  const values = [
    {
      icon: '❤️',
      title: 'Made with Love',
      description: 'Every kulfi and ice cream is crafted with passion and care, just like homemade.'
    },
    {
      icon: '🌾',
      title: 'Quality Ingredients',
      description: 'We source only the finest ingredients - from farm-fresh milk to premium dry fruits.'
    },
    {
      icon: '👨‍👩‍👧‍👦',
      title: 'Family Tradition',
      description: 'Our recipes have been passed down through generations, preserving authentic taste.'
    },
    {
      icon: '🌿',
      title: 'Eco-Friendly',
      description: 'We\'re committed to sustainable practices and eco-friendly packaging.'
    }
  ];

  const team = [
    {
      name: 'Grandma\'s Recipe',
      role: 'The Secret Behind Our Taste',
      image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=300&h=300&fit=crop',
      description: 'Our authentic recipes come from traditional family wisdom'
    },
    {
      name: 'Fresh Every Day',
      role: 'Quality Commitment',
      image: 'https://images.unsplash.com/photo-1556909114-44e3e70034e2?w=300&h=300&fit=crop',
      description: 'Made fresh daily with no artificial preservatives'
    },
    {
      name: 'Happy Customers',
      role: 'Our Greatest Achievement',
      image: 'https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e?w=300&h=300&fit=crop',
      description: 'Your smiles are what keep us going'
    }
  ];

  return (
    <>
      {/* About Hero */}
      <section className="about-hero">
        <div className="container">
          <div className="about-content">
            <div className="about-text slide-in-left">
              <h1 className="gradient-text">Our Sweet Story</h1>
              <p>
                Welcome to <strong>Kitto Di Kulfi</strong>, where every scoop tells a story of 
                tradition, quality, and pure happiness. What started as a small family dream 
                has grown into a beloved destination for kulfi and ice cream lovers.
              </p>
              <p>
                Our journey began with a simple belief - that the best desserts are made with 
                love, using time-honored recipes and the finest natural ingredients. Today, 
                we continue that tradition, serving smiles one kulfi at a time.
              </p>
              <Link to="/menu" className="btn btn-primary" style={{ marginTop: '20px' }}>
                Explore Our Menu 🍨
              </Link>
            </div>
            <div className="about-image slide-in-right">
              <img src="/logo.jpeg" alt="Kitto Di Kulfi" />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="story-section">
        <div className="container">
          <div className="section-header fade-in">
            <h2 className="gradient-text">Our Journey in Numbers</h2>
            <p>A decade of creating sweet memories</p>
          </div>
          
          <div className="story-grid">
            {stats.map((stat, index) => (
              <div 
                key={index} 
                className="story-card fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <span className="story-number">{stat.number}</span>
                <h3>{stat.label}</h3>
                <p>{stat.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="section features-section">
        <div className="container">
          <div className="section-header fade-in">
            <h2>What We Stand For</h2>
            <p>The principles that guide everything we do</p>
          </div>
          
          <div className="features-grid">
            {values.map((value, index) => (
              <div 
                key={index} 
                className="feature-card fade-in"
                style={{ animationDelay: `${index * 0.15}s` }}
              >
                <span className="feature-icon">{value.icon}</span>
                <h3>{value.title}</h3>
                <p>{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story Timeline */}
      <section className="section" style={{ background: 'white' }}>
        <div className="container">
          <div className="section-header fade-in">
            <h2 className="gradient-text">The Kitto Di Kulfi Story</h2>
            <p>From humble beginnings to your favorite kulfi destination</p>
          </div>

          <div style={{ 
            maxWidth: '800px', 
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '40px'
          }}>
            {[
              {
                year: '2014',
                title: 'The Beginning',
                description: 'Started as a small kulfi stall with grandma\'s secret recipes and big dreams.'
              },
              {
                year: '2016',
                title: 'Growing Love',
                description: 'Word spread fast! Opened our first proper parlour due to overwhelming demand.'
              },
              {
                year: '2019',
                title: 'Menu Expansion',
                description: 'Introduced ice creams, shakes, and sundaes while keeping kulfi at our heart.'
              },
              {
                year: '2023',
                title: 'Community Favorite',
                description: 'Became the go-to destination for celebrations and sweet cravings alike.'
              },
              {
                year: '2024',
                title: 'Looking Ahead',
                description: 'Continuing to innovate while staying true to our roots. The journey continues!'
              }
            ].map((milestone, index) => (
              <div 
                key={index}
                className="fade-in"
                style={{
                  display: 'flex',
                  gap: '30px',
                  alignItems: 'flex-start'
                }}
              >
                <div style={{
                  minWidth: '80px',
                  height: '80px',
                  background: 'linear-gradient(135deg, var(--pink-primary), var(--orange-primary))',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontFamily: 'var(--font-display)',
                  fontWeight: '700',
                  fontSize: '1.2rem',
                  boxShadow: 'var(--shadow-medium)'
                }}>
                  {milestone.year}
                </div>
                <div style={{
                  background: 'var(--cream)',
                  padding: '25px 30px',
                  borderRadius: '20px',
                  flex: 1
                }}>
                  <h3 style={{ marginBottom: '10px', color: 'var(--chocolate)' }}>{milestone.title}</h3>
                  <p style={{ opacity: 0.8 }}>{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team/Gallery Section */}
      <section className="section gallery-section">
        <div className="container">
          <div className="section-header fade-in">
            <h2 className="gradient-text">Behind Every Scoop</h2>
            <p>The passion and people that make Kitto Di Kulfi special</p>
          </div>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '30px'
          }}>
            {team.map((member, index) => (
              <div 
                key={index}
                className="fade-in"
                style={{
                  background: 'white',
                  borderRadius: '25px',
                  overflow: 'hidden',
                  boxShadow: 'var(--shadow-soft)',
                  transition: 'var(--transition-smooth)'
                }}
              >
                <img 
                  src={member.image} 
                  alt={member.name}
                  style={{
                    width: '100%',
                    height: '250px',
                    objectFit: 'cover'
                  }}
                />
                <div style={{ padding: '25px', textAlign: 'center' }}>
                  <h3 style={{ marginBottom: '5px' }}>{member.name}</h3>
                  <p style={{ 
                    color: 'var(--pink-primary)', 
                    fontWeight: '600',
                    marginBottom: '10px'
                  }}>
                    {member.role}
                  </p>
                  <p style={{ opacity: 0.7, fontSize: '0.95rem' }}>{member.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section features-section" style={{ padding: '80px 0' }}>
        <div className="container">
          <div className="fade-in" style={{ textAlign: 'center' }}>
            <h2 style={{ fontSize: '2.5rem', marginBottom: '20px' }}>
              Come Be Part of Our Story! 🍦
            </h2>
            <p style={{ 
              fontSize: '1.2rem', 
              opacity: 0.9, 
              marginBottom: '35px', 
              maxWidth: '600px', 
              margin: '0 auto 35px' 
            }}>
              Visit us today and experience the magic of authentic kulfi and artisan ice cream.
            </p>
            <Link to="/contact" className="btn" style={{ 
              background: 'white', 
              color: 'var(--pink-primary)',
              boxShadow: '0 8px 25px rgba(0,0,0,0.2)'
            }}>
              📍 Find Us
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default About;
